import { useEffect, useMemo, useState } from 'react';
import { Drawer } from '../components/data';
import { ErrorBanner, Loading } from '../components/ui';
import { api } from '../lib/api';
import map from '../lib/autotaskEntities.json';

/**
 * The Autotask Data Map's live side: an entity's records, one record with
 * every field, and from there any record it points at or that points at it.
 * Read-only — see routes/autotaskExplorer.ts.
 */

interface EntityField {
  name: string;
  type: string;
  reference: string | null;
  picklist: boolean;
}
interface Entity {
  name: string;
  fields: EntityField[];
}
const ENTITIES = (map as { entities: Entity[] }).entities;
const BY_NAME = new Map(ENTITIES.map((e) => [e.name, e]));

type Labels = Record<string, Record<string, string>>;
type Rec = Record<string, unknown> & { id: number };

/* The field that names a record, best first — whichever the entity has. */
const NAME_FIELDS = [
  'ticketNumber', 'companyName', 'projectName', 'contractName', 'referenceTitle', 'title', 'name',
  'invoiceNumber', 'purchaseOrderNumber', 'quoteNumber', 'description', 'summaryNotes', 'noteText',
];
/* Worth a column in a list, when the entity has them. */
const LIST_FIELDS = [
  'title', 'status', 'companyID', 'contactID', 'assignedResourceID', 'resourceID', 'ticketID', 'projectID',
  'isActive', 'createDate', 'createDateTime', 'dateWorked', 'itemDate', 'lastActivityDate', 'hoursWorked',
  'extendedPrice', 'amount', 'email', 'emailAddress',
];

export function recordName(entity: string, r: Rec): string {
  const fields = new Set((BY_NAME.get(entity)?.fields ?? []).map((f) => f.name));
  if (fields.has('firstName') && fields.has('lastName') && (r.firstName || r.lastName)) {
    return `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim();
  }
  for (const f of NAME_FIELDS) {
    const v = r[f];
    if (fields.has(f) && v !== null && v !== undefined && String(v).trim()) {
      const text = String(v).replace(/\s+/g, ' ').trim();
      return entity === 'Tickets' && r.title ? `${text} — ${r.title}` : text.length > 90 ? `${text.slice(0, 89)}…` : text;
    }
  }
  return `${entity} ${r.id}`;
}

function show(entity: string, field: string, value: unknown, labels: Labels): string {
  if (value === null || value === undefined || value === '') return '';
  const def = BY_NAME.get(entity)?.fields.find((f) => f.name === field);
  if (def?.picklist) {
    const label = labels[field.toLowerCase()]?.[String(value)];
    return label ? `${label} (${String(value)})` : String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(/(Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** A page of an entity's records, optionally narrowed to `field` = `value`. */
export function RecordList({
  entity,
  field,
  value,
  onOpen,
}: {
  entity: string;
  field?: string;
  value?: string | number;
  onOpen: (entity: string, id: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [after, setAfter] = useState(0);
  const [trail, setTrail] = useState<number[]>([]);
  const [data, setData] = useState<{ records: Rec[]; labels: Labels; next: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const def = BY_NAME.get(entity);
  const hasActive = !!def?.fields.some((x) => x.name === 'isActive');
  const nameField = NAME_FIELDS.find((f) => def?.fields.some((x) => x.name === f) && def.fields.find((x) => x.name === f)?.type.startsWith('string'));
  const columns = useMemo(
    () => LIST_FIELDS.filter((f) => def?.fields.some((x) => x.name === f) && f !== nameField && f !== field).slice(0, 4),
    [def, nameField, field],
  );

  useEffect(() => {
    setAfter(0);
    setTrail([]);
  }, [entity, field, value, query, activeOnly]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ after: String(after), limit: '25' });
    if (field && value !== undefined) {
      params.set('field', field);
      params.set('value', String(value));
    } else if (query && nameField) {
      params.set('field', nameField);
      params.set('op', 'contains');
      params.set('value', query);
    }
    if (activeOnly && hasActive) params.set('active', '1');
    api
      .get<{ records: Rec[]; labels: Labels; next: number | null }>(`/autotask-explore/${entity}/records?${params}`)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not read Autotask'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [entity, field, value, query, after, nameField, activeOnly, hasActive]);

  const searchable = !field && !!nameField;

  return (
    <div>
      {(searchable || hasActive) && (
        <div className="row wrap mb-2">
          {searchable && (
          <>
          <input
            className="input"
            placeholder={`Search ${entity} by ${nameField}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setQuery(search.trim());
            }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery(search.trim())}>
            Search
          </button>
          {query && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setQuery(''); }}>
              Clear
            </button>
          )}
          </>
          )}
          {hasActive && (
            <label className="checkbox small">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              Active only
            </label>
          )}
        </div>
      )}
      {error && <ErrorBanner message={error} />}
      {loading && !data && <Loading label={`Reading ${entity} from Autotask`} />}
      {data && (
        <>
          {data.records.length === 0 ? (
            <p className="muted small">No {activeOnly && hasActive ? 'active ' : ''}{entity} {field ? `with ${field} = ${String(value)}` : query ? `matching “${query}”` : ''}.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Record</th>
                    {columns.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.records.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <button type="button" className="link-button" onClick={() => onOpen(entity, r.id)}>
                          {recordName(entity, r)}
                        </button>
                        <div className="xs muted mono">id {r.id}</div>
                      </td>
                      {columns.map((c) => {
                        const ref = def?.fields.find((x) => x.name === c)?.reference;
                        const v = r[c];
                        return (
                          <td key={c} className="xs">
                            {ref && v && BY_NAME.has(ref) ? (
                              <button type="button" className="link-button" onClick={() => onOpen(ref, Number(v))}>
                                {ref} {String(v)}
                              </button>
                            ) : (
                              show(entity, c, v, data.labels) || <span className="subtle">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row mt-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={trail.length === 0 || loading}
              onClick={() => {
                const t = [...trail];
                const prev = t.pop() ?? 0;
                setTrail(t);
                setAfter(prev);
              }}
            >
              ← Previous
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={data.next === null || loading}
              onClick={() => {
                setTrail((t) => [...t, after]);
                setAfter(data.next ?? 0);
              }}
            >
              Next 25 →
            </button>
            <span className="xs muted">Oldest first, as Autotask sorts by id.</span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A record in a side panel, every field, with a trail back through the
 * records it was reached from. References open their record; "Records that
 * point here" lists what references this one.
 */
export function RecordDrawer({
  stack,
  onClose,
  onOpen,
  onBack,
}: {
  stack: { entity: string; id: number }[];
  onClose: () => void;
  onOpen: (entity: string, id: number) => void;
  onBack: (depth: number) => void;
}) {
  const top = stack[stack.length - 1];
  const [data, setData] = useState<{ record: Rec; labels: Labels } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [related, setRelated] = useState<{ entity: string; field: string } | null>(null);

  useEffect(() => {
    if (!top) return;
    let live = true;
    setData(null);
    setError(null);
    setRelated(null);
    api
      .get<{ record: Rec; labels: Labels }>(`/autotask-explore/${top.entity}/records/${top.id}`)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not read Autotask'));
    return () => {
      live = false;
    };
  }, [top?.entity, top?.id]);

  if (!top) return null;
  const def = BY_NAME.get(top.entity);
  const pointsHere = ENTITIES.flatMap((e) =>
    e.fields.filter((f) => f.reference === top.entity).map((f) => ({ entity: e.name, field: f.name })),
  );

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={data ? recordName(top.entity, data.record) : `${top.entity} ${top.id}`}
      subtitle={`${top.entity} · id ${top.id}`}
    >
      {stack.length > 1 && (
        <div className="atmap-trail mb-2">
          {stack.map((s, i) => (
            <span key={`${s.entity}-${s.id}-${i}`}>
              {i > 0 && ' › '}
              {i < stack.length - 1 ? (
                <button type="button" className="link-button" onClick={() => onBack(i + 1)}>
                  {s.entity} {s.id}
                </button>
              ) : (
                <strong>
                  {s.entity} {s.id}
                </strong>
              )}
            </span>
          ))}
        </div>
      )}
      {error && <ErrorBanner message={error} />}
      {!data && !error && <Loading label="Reading the record from Autotask" />}
      {data && (
        <>
          <label className="checkbox small mb-2">
            <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />
            Show empty fields
          </label>
          <table className="table atmap-record">
            <tbody>
              {(def?.fields ?? [])
                .map((f) => ({ f, v: data.record[f.name] }))
                .filter(({ v }) => showEmpty || (v !== null && v !== undefined && v !== ''))
                .map(({ f, v }) => (
                  <tr key={f.name}>
                    <th className="mono xs">{f.name}</th>
                    <td className="small">
                      {f.reference && v && BY_NAME.has(f.reference) ? (
                        <button type="button" className="link-button" onClick={() => onOpen(f.reference!, Number(v))}>
                          {f.reference} {String(v)} →
                        </button>
                      ) : (
                        show(top.entity, f.name, v, data.labels) || <span className="subtle">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              {Array.isArray(data.record.userDefinedFields) &&
                (data.record.userDefinedFields as { name: string; value: unknown }[])
                  .filter((u) => showEmpty || (u.value !== null && u.value !== ''))
                  .map((u) => (
                    <tr key={`udf-${u.name}`}>
                      <th className="xs">{u.name} <span className="muted">(your field)</span></th>
                      <td className="small">{u.value === null ? <span className="subtle">—</span> : String(u.value)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>

          {pointsHere.length > 0 && (
            <div className="mt-4">
              <div className="xs strong mb-2">Records that point here</div>
              <div className="atmap-chips mb-2">
                {pointsHere.map((p) => (
                  <button
                    key={`${p.entity}.${p.field}`}
                    type="button"
                    className={`atmap-chip${related?.entity === p.entity && related.field === p.field ? ' is-on' : ''}`}
                    onClick={() => setRelated(related?.entity === p.entity && related.field === p.field ? null : p)}
                    title={`${p.entity} whose ${p.field} is ${top.id}`}
                  >
                    {p.entity} <span className="atmap-chip-n">{p.field}</span>
                  </button>
                ))}
              </div>
              {related && <RecordList entity={related.entity} field={related.field} value={top.id} onOpen={onOpen} />}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
